import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Building2, MapPin, Calendar, X } from "lucide-react";

// Public Mapbox API Key from WorldMap.js
const MAPBOX_API_KEY = "pk.eyJ1IjoiZGFyaWVsYWZzIiwiYSI6ImNtaWZ6OWk2dzAxZGgzY3F1YmRmMmF3aGcifQ.mEM3q--ybjVUtbOXZGVBGA";

const GlobeMap = ({ locations = [], className = "" }) => {
  const wrapperRef = useRef(null);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const spinGlobeRef = useRef(null);
  const spinTimeoutRef = useRef(null);
  const dashAnimationRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [userInteracting, setUserInteracting] = useState(false);

  // Default locations if none provided
  const defaultLocations = [
    {
      name: "Siège Social",
      city: "Douala",
      country: "Cameroun",
      type: "Bureau Principal",
      year: "2017",
      coordinates: [9.7085, 4.0511],
      description: "Siège social et centre opérationnel principal",
    },
    {
      name: "Bureau Régional",
      city: "Nairobi",
      country: "Kenya",
      type: "Filiale",
      year: "2021",
      coordinates: [36.8219, -1.2921],
      description: "Expansion en Afrique de l'Est",
    },
    {
      name: "Bureau Régional",
      city: "Lagos",
      country: "Nigeria",
      type: "Filiale",
      year: "2019",
      coordinates: [3.3792, 6.5244],
      description: "Hub Afrique de l'Ouest",
    },
    {
      name: "Bureau Régional",
      city: "Cotonou",
      country: "Bénin",
      type: "Filiale",
      year: "2020",
      coordinates: [2.3158, 6.3703],
      description: "Opérations régionales",
    },
    {
      name: "Bureau Régional",
      city: "Johannesburg",
      country: "Afrique du Sud",
      type: "Filiale",
      year: "2024",
      coordinates: [28.0473, -26.2041],
      description: "Expansion en Afrique Australe",
    }
  ];

  const activeLocations = locations.length > 0 ? locations : defaultLocations;

  const spinGlobe = () => {
    if (!map.current) return;

    const secondsPerRevolution = 180;
    const maxSpinZoom = 5;
    const slowSpinZoom = 3;
    const zoom = map.current.getZoom();

    if (!userInteracting && zoom < maxSpinZoom) {
      let distancePerSecond = 360 / secondsPerRevolution;
      if (zoom > slowSpinZoom) {
        const zoomDif = (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
        distancePerSecond *= zoomDif;
      }
      const center = map.current.getCenter();
      center.lng -= distancePerSecond;

      map.current.easeTo({
        center,
        duration: 3000,
        easing: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
        animate: true,
      });
    }

    spinGlobeRef.current = requestAnimationFrame(spinGlobe);
  };

  const startSpinGlobe = () => {
    if (spinGlobeRef.current) return;
    spinGlobeRef.current = requestAnimationFrame(spinGlobe);
  };

  const stopSpinGlobe = () => {
    if (spinGlobeRef.current) {
      cancelAnimationFrame(spinGlobeRef.current);
      spinGlobeRef.current = null;
    }
  };

  useEffect(() => {
    if (!mapContainer.current) return;

    mapboxgl.accessToken = MAPBOX_API_KEY;

    const resizeMap = () => {
      try {
        map.current?.resize();
      } catch {
        // ignore resize errors during teardown
      }
    };

    let resizeObserver = null;
    const handleWindowResize = () => resizeMap();

    if (typeof ResizeObserver !== "undefined" && wrapperRef.current) {
      resizeObserver = new ResizeObserver(() => resizeMap());
      resizeObserver.observe(wrapperRef.current);
    } else {
      window.addEventListener("resize", handleWindowResize);
    }

    // Création de la carte SANS les attributions
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/dark-v11",
      projection: { name: "globe" },
      zoom: 1.8,
      center: [20, 5],
      pitch: 25,
      interactive: true,
      dragRotate: true,
      touchZoomRotate: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      dragPan: true,
      maxZoom: 18,
      minZoom: 0.5,
      maxPitch: 85,
      minPitch: 0,
      // SUPPRIME LES TEXTES MAPBOX ET OPENSTREETMAP
      attributionControl: false
    });

    // Ajouter les contrôles de navigation
    map.current.addControl(
      new mapboxgl.NavigationControl({
        visualizePitch: true,
        showCompass: true,
        showZoom: true,
      }),
      "top-right"
    );

    // Ajouter un contrôle d'attribution personnalisé (optionnel - peut être vide)
    // Si vous voulez garder une attribution légale minimale
    // map.current.addControl(
    //   new mapboxgl.AttributionControl({
    //     compact: true,
    //     customAttribution: ''
    //   }),
    //   'bottom-right'
    // );

    // Cacher complètement les attributions avec CSS (sécurité supplémentaire)
    const styleElement = document.createElement('style');
    styleElement.id = 'hide-mapbox-attribution';
    styleElement.textContent = `
      .mapboxgl-ctrl-attrib {
        display: none !important;
      }
      .mapboxgl-ctrl-attrib-inner {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);

    map.current.on("style.load", () => {
      let breathIntensity = 0;
      let breathDirection = 1;

      const updateBreathEffect = () => {
        breathIntensity += 0.005 * breathDirection;
        if (breathIntensity >= 1) breathDirection = -1;
        else if (breathIntensity <= 0) breathDirection = 1;

        map.current?.setFog({
          color: `rgb(${20 + breathIntensity * 30}, ${180 + breathIntensity * 40}, ${100 + breathIntensity * 50})`,
          "high-color": `rgb(${40 + breathIntensity * 40}, ${200 + breathIntensity * 30}, ${120 + breathIntensity * 40})`,
          "horizon-blend": 0.1 + breathIntensity * 0.05,
          "space-color": `rgb(${5 + breathIntensity * 10}, ${20 + breathIntensity * 15}, ${15 + breathIntensity * 10})`,
          "star-intensity": 0.3 + breathIntensity * 0.2,
        });

        requestAnimationFrame(updateBreathEffect);
      };

      updateBreathEffect();
      map.current?.setRenderWorldCopies(false);
    });

    let resetSpinTimer = null;
    const handleUserInteractionStart = () => {
      setUserInteracting(true);
      stopSpinGlobe();
      if (resetSpinTimer) {
        clearTimeout(resetSpinTimer);
        resetSpinTimer = null;
      }
    };

    const handleUserInteractionEnd = () => {
      setUserInteracting(false);
      resetSpinTimer = setTimeout(() => {
        startSpinGlobe();
      }, 3000);
    };

    map.current.on("mousedown", handleUserInteractionStart);
    map.current.on("dragstart", handleUserInteractionStart);
    map.current.on("touchstart", handleUserInteractionStart);
    map.current.on("zoomstart", handleUserInteractionStart);
    map.current.on("load", () => {
      if (!map.current) return;

      resizeMap();

      const doualaCoordinates = activeLocations[0].coordinates;
      map.current.easeTo({
        center: doualaCoordinates,
        zoom: 3.5,
        duration: 3000,
        easing: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
      });

      spinTimeoutRef.current = setTimeout(startSpinGlobe, 5000);

      const connectionLines = activeLocations.slice(1).map((loc) => ({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: [doualaCoordinates, loc.coordinates],
        },
      }));

      map.current.addSource("connections", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: connectionLines,
        },
      });

      map.current.addLayer({
        id: "connections-line",
        type: "line",
        source: "connections",
        paint: {
          "line-color": "#16A34A",
          "line-width": 2,
          "line-opacity": 0.3,
        },
      });

      map.current.addLayer({
        id: "connections-animated",
        type: "line",
        source: "connections",
        paint: {
          "line-color": "#16A34A",
          "line-width": 2.5,
          "line-opacity": 0.8,
          "line-dasharray": [3, 6],
        },
      });

      let dashOffset = 0;
      const animateDash = () => {
        if (!map.current) return;
        dashOffset = (dashOffset + 0.1) % 6;

        try {
          if (map.current.isStyleLoaded?.() && map.current.getLayer?.("connections-animated")) {
            map.current.setPaintProperty("connections-animated", "line-dasharray", [0.5 + Math.abs(Math.sin(dashOffset)) * 2, 4]);
          }
        } catch {
          // Map might be tearing down; ignore.
        }

        dashAnimationRef.current = requestAnimationFrame(animateDash);
      };
      animateDash();

      activeLocations.forEach((location, index) => {
        if (!map.current) return;

        const el = document.createElement("div");
        el.className = "globe-marker";
        el.style.width = index === 0 ? "28px" : "24px";
        el.style.height = index === 0 ? "28px" : "24px";
        el.style.borderRadius = "50%";
        el.style.backgroundColor = "#fff";
        el.style.border = "4px solid #16A34A";
        el.style.cursor = "pointer";
        el.style.boxShadow = "0 0 20px rgba(22, 163, 74, 0.6)";
        el.style.transition = "all 0.3s ease";

        if (index === 0) {
          el.style.animation = "globe-pulse 3s infinite";
        }

        el.addEventListener("click", (e) => {
          e.stopPropagation();
          setSelectedLocation(location);
          map.current.flyTo({
            center: location.coordinates,
            zoom: 8,
            speed: 0.8,
          });
        });

        const marker = new mapboxgl.Marker(el).setLngLat(location.coordinates).addTo(map.current);
        markersRef.current.push(marker);
      });

      setMapReady(true);
    });

    // Cleanup
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
      if (dashAnimationRef.current) cancelAnimationFrame(dashAnimationRef.current);
      if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
      if (spinGlobeRef.current) cancelAnimationFrame(spinGlobeRef.current);
      try {
        markersRef.current.forEach((marker) => marker.remove());
      } catch {
        // ignore marker teardown errors
      }

      const instance = map.current;
      map.current = null;

      // Remove every mapbox-created node (canvas, controls, panes) BEFORE the
      // mapbox instance is destroyed, so React's own removal of the container
      // never runs into nodes that mapbox has already detached. Doing this on
      // the raw container keeps mapbox from touching the React-managed tree.
      const container = mapContainer.current;
      if (container) {
        try {
          container.replaceChildren();
        } catch {
          // ignore
        }
      }

      // Destroy the mapbox instance (listeners, WebGL context, workers).
      // Wrapped in try/catch so a partially torn-down map can never throw a
      // NotFoundError that React would turn into a render/error-boundary crash.
      if (instance) {
        try {
          instance.remove();
        } catch {
          // map may already be torn down
        }
      }

      // Supprimer le style d'attribution ajouté
      const styleToRemove = document.getElementById('hide-mapbox-attribution');
      if (styleToRemove) styleToRemove.remove();
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`relative w-full h-[500px] md:h-[600px] rounded-2xl overflow-hidden bg-slate-900 shadow-2xl group ${className}`}
    >
      <div
        ref={mapContainer}
        className="absolute inset-0"
        style={{ opacity: mapReady ? 1 : 0, transition: "opacity 1s" }}
      />

      <button
        onClick={() => {
          if (!map.current) return;
          map.current.easeTo({ center: activeLocations[0].coordinates, zoom: 3.5, duration: 2000 });
          setSelectedLocation(null);
        }}
        className="absolute top-4 right-16 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10 backdrop-blur-sm"
        aria-label="Recentrer"
      >
        <MapPin className="h-5 w-5 text-white" />
      </button>

      {selectedLocation && (
        <div className="absolute top-4 left-4 p-4 min-w-[280px] bg-slate-900/90 border border-emerald-500/50 rounded-xl shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-left-4 z-20 text-white">
          <button
            onClick={() => setSelectedLocation(null)}
            className="absolute top-2 right-2 p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">{selectedLocation.name}</h3>
              <p className="text-sm text-emerald-400 font-medium">{selectedLocation.type}</p>
            </div>
          </div>
          <div className="space-y-2 text-sm text-slate-300">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald-500" />
              <span>{selectedLocation.city}, {selectedLocation.country}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-emerald-500" />
              <span>Établi en {selectedLocation.year}</span>
            </div>
            <p className="mt-3 pt-3 border-t border-slate-700 text-slate-400 italic">
              {selectedLocation.description}
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes globe-pulse {
          0% { box-shadow: 0 0 20px rgba(22, 163, 74, 0.6); }
          50% { box-shadow: 0 0 40px rgba(22, 163, 74, 1); }
          100% { box-shadow: 0 0 20px rgba(22, 163, 74, 0.6); }
        }
        .globe-marker:hover {
          transform: scale(1.2);
          background-color: #16A34A !important;
          border-color: #fff !important;
        }
      `}</style>
    </div>
  );
};

export default GlobeMap;